import './util/embeddedBundleSession'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ErrorBoundary } from 'react-error-boundary'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import ThemeProvider from './theme/ThemeProvider'
import ErrorFallback from './ErrorFallback'
import ROUTES from './assets/routes'
import EmbeddedWrapper from './embeddedWrapper'
import { isEmbeddedModeSet } from './util/general'
import SecurityManager from './util/securityManager'
import { UserProvider } from './context/UserContext'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import UpiPaymentPage from './pages/upiPayment/UpiPaymentPage'
import store, { persistor } from './store'

const embeddedMode = isEmbeddedModeSet()
const routerBaseName = import.meta.env.BASE_URL === '/' ? undefined : import.meta.env.BASE_URL.replace(/\/$/, '')

const pathname = window.location.pathname
const pathForMatch = routerBaseName && pathname.startsWith(routerBaseName)
    ? pathname.slice(routerBaseName.length) || '/'
    : pathname
const isUpiPaymentRoute = pathForMatch.startsWith('/upi-payment/')

const root = ReactDOM.createRoot(document.getElementById('root')!)

window.securityManager = new SecurityManager()

const isProductionDeployed = import.meta.env.MODE === 'production' && !window.location.origin.includes('localhost')
const debugLogsEnabled = import.meta.env.VITE_DEBUG_LOGS === 'true'
if (isProductionDeployed && !debugLogsEnabled) {
    console.log = () => {}
    console.debug = () => {}
    console.warn = () => {}
}

const errorBoundary = (children: React.ReactNode) => (
    <ErrorBoundary
        key="app-fallback"
        FallbackComponent={ErrorFallback}
        onReset={(reset) => {
            //@ts-ignore
            if (reset?.args?.[0]) {
                window.location.href = import.meta.env.BASE_URL
            } else {
                window.location.reload()
            }
        }}
    >
        {children}
    </ErrorBoundary>
)

// Lightweight boot for UPI payment tab — skip PersistGate / WebSocket / full App (~10s).
if (isUpiPaymentRoute) {
    root.render(
        <Provider store={store}>
            <ThemeProvider>
                <BrowserRouter basename={routerBaseName}>
                    {errorBoundary(
                        <Routes>
                            <Route path={ROUTES.upiPayment} element={<UpiPaymentPage />} />
                        </Routes>
                    )}
                </BrowserRouter>
            </ThemeProvider>
        </Provider>
    )
} else {
    // Heavy chart/drawing constants only needed for the main trading app.
    void import('./components/chartV2/drawingTools/drawing.constants')

    void Promise.all([
        import('./App'),
        import('./components/webSocketConnection/WebSocket'),
        import('./components/KeyboardShortcuts/Shortcuts.context'),
    ]).then(([
        { default: App },
        { default: WebSocketProvider },
        { default: ShortcutsProvider },
    ]) => {
        const app = (
            <WebSocketProvider>
                <ShortcutsProvider>
                    <App key="tick2trade" />
                </ShortcutsProvider>
            </WebSocketProvider>
        )

        root.render(
            <Provider store={store}>
                <ThemeProvider>
                    <UserProvider>
                        <BrowserRouter basename={routerBaseName}>
                            <EmbeddedWrapper>
                                {errorBoundary(
                                    embeddedMode ? (
                                        app
                                    ) : (
                                        <PersistGate loading={null} persistor={persistor}>
                                            {app}
                                        </PersistGate>
                                    )
                                )}
                            </EmbeddedWrapper>
                        </BrowserRouter>
                    </UserProvider>
                </ThemeProvider>
            </Provider>
        )
    })
}

serviceWorkerRegistration.disableServiceWorker()
